import PropTypes from 'prop-types'
import { useCallback, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { DUAL_PANEL_SELECTABLE_TYPES } from '../../../constants/dual-panel-selector-constants'
import { fullTeeYardageByLevel, yardageByLevel } from '../../../utility/curriculumHelper'
import { getStudentsDualPanelSelector } from '../../../actions/communitiesActions'
import DualPanelSelector from '../../common/dual-panel-selector/DualPanelSelector'
import { addStudent, addSelectedStudent, deleteSelectedStudent, updateStudent, resetSelectedStudents } from '../../../reducers/nineHoleEventReducer'
import { setUnavailableStudents } from '../../../reducers/dualPanelSelectorReducer'
import classnames from 'classnames'
import Modal from '../../common/Modal'
import SubmitButton from '../../common/buttons/SubmitButton'

import classes from './AddGolferModal.module.scss'
import { ReactComponent as PersonIcon } from '../../../assets/images/common/icons/person-icon.svg'

const SummaryPlaceholder = () => {
  const { t } = useTranslation()

  return (
    <div className={classes.placeholder}>
      <PersonIcon className={classes.icon} />
      <p className={classes.title}>{t('events.form.addGolferModal.placeholderTitle')}</p>
      <p className={classes.description}>
        {t('events.form.addGolferModal.placeholderDescription')}
      </p>
    </div>
  )
}

const AddGolferModal = ({
  title,
  ...props
}) => {
  const { t } = useTranslation()
  const communityId = useSelector(state => state.user.current_user.communities[0].id)
  const event = useSelector(state => state.nineHoleEvent)
  const dispatch = useDispatch()
  const [studentsData, setStudentsData] = useState({ searchValue: '', students: null, pagination: { page: 0, is_last_page: false } })

  const getSelectedStudent = (id) => event.selectedStudents.find(student => student.id === id)

  useEffect(() => {
    if (!props.isOpen) { return }

    dispatch(resetSelectedStudents())
    dispatch(setUnavailableStudents([...event.students].filter(s => s.status !== 'declined')))
  }, [dispatch, event.students, props.isOpen])

  const fetchStudents = useCallback((page, searchTerm) => getStudentsDualPanelSelector({
    page,
    perPage: 10,
    communityId,
    searchTerm,
  })
    .then(data => setStudentsData(prevState => ({
      ...prevState,
      students: page === 1 ?
        [...data.students].filter(s => s.status !== 'declined') :
        [...prevState.students, ...data.students.filter(s => s.status !== 'declined')],
      pagination: data.meta.pagination,
    })))
    .catch(() => toast.error(t('common.somethingWentWrong'), { position: toast.POSITION.TOP_RIGHT }))
  , [communityId, t])

  const handleAddStudent = (std) => {
    const studentData = {
      ...std,
      type: DUAL_PANEL_SELECTABLE_TYPES.students,
      level: std.currentFormalDivision,
      yardage: yardageByLevel[std.currentFormalDivision],
      fullTeeYardage: fullTeeYardageByLevel[std.currentFormalDivision],
    }

    dispatch(addSelectedStudent({ ...studentData, addedFrom: { students: true }, status: 'created' }))
  }

  const handleDeleteStudent = (std) => {
    dispatch(deleteSelectedStudent(std.id))
  }

  const handleCheckStudent = (checked, student) => {
    if (checked) {
      handleDeleteStudent(student)
    } else if (student.status !== 'invited') {
      handleAddStudent(student)
    }
  }

  const handleStudentsSearchChange = (searchValue) => setStudentsData(prevState => ({ ...prevState, searchValue }))

  const onProceed = () => {
    event.selectedStudents.forEach( std => {
      const index = event.students.findIndex(s => s.id === std.id)
      if (index === -1) {
        dispatch(addStudent(std))
      } else {
        dispatch(updateStudent(std))
      }
    })
  }

  const leftPanelProps = {
    tabTypes: [DUAL_PANEL_SELECTABLE_TYPES.programsWithPackages, DUAL_PANEL_SELECTABLE_TYPES.students],
    tabProps: {
      [DUAL_PANEL_SELECTABLE_TYPES.students]: {
        allItems: studentsData.students,
        searchValue: studentsData.searchValue,
        onSearchValueChange: handleStudentsSearchChange,
        getSelectedItem: getSelectedStudent,
        fetchItems: fetchStudents,
        pagination: studentsData.pagination,
        onCheckChange: handleCheckStudent,
        isLoading: studentsData.isLoading,
      },
    },
    withSwitcherInsteadOfTabs: true,
    switcherPageTitle: title,
  }

  const rightPanelProps = {
    selectedItems: event.selectedStudents,
    selectedItemType: DUAL_PANEL_SELECTABLE_TYPES.students,
    summaryTitle: t('events.form.addGolferModal.summaryTitle', { count: event.selectedStudents.length }),
    summaryPlaceholder: <SummaryPlaceholder />,
    onRemove: (student) => handleCheckStudent(true, student),
  }

  return (
    <Modal className={classes.modal} title={t('events.form.addGolferModal.title')} {...props}>
      <div className={classnames('d-flex flex-column', classes.modalContentWrapper)}>
        <DualPanelSelector
          type='AddGolferRoster'
          className={classes.dualPanelSelector}
          {...leftPanelProps}
          {...rightPanelProps}
        />
        <div className='mt-3 d-flex justify-content-end'>
          <SubmitButton
            disabled={!event.selectedStudents.length}
            className={classes.button}
            buttonMessage={t('events.form.addGolferModal.addStudents')}
            onClick={() => {
              props.onClose()
              onProceed()
            }}
          />
        </div>
      </div>
    </Modal>
  )
}

AddGolferModal.propTypes = {
  title: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
}

export default AddGolferModal
